import {
  ESG_STORAGE_ACCOUNT_NAME,
  ESG_STORAGE_CONTAINER,
  FILE_STORAGE_HOST,
} from './config';
import { generatePreSignedUrl } from './portalApi';

const encodeBlobPath = (blobPath) => blobPath.split('/').map(encodeURIComponent).join('/');

const blobBaseUrl = (blobPath) =>
  `https://${FILE_STORAGE_HOST}/${ESG_STORAGE_CONTAINER}/${encodeBlobPath(blobPath)}`;

export const buildEsgDocumentBlobUrl = (blobPath) => blobBaseUrl(blobPath);

export function parseEsgDocumentBlobPath(blobUrlOrPath) {
  if (!blobUrlOrPath) return '';
  if (!/^https?:\/\//i.test(blobUrlOrPath)) return blobUrlOrPath;

  try {
    const pathname = decodeURIComponent(new URL(blobUrlOrPath).pathname);
    const containerPrefix = `/${ESG_STORAGE_CONTAINER}/`;
    const containerIndex = pathname.indexOf(containerPrefix);
    if (containerIndex !== -1) {
      return pathname.slice(containerIndex + containerPrefix.length);
    }
  } catch {
    return blobUrlOrPath;
  }

  return blobUrlOrPath;
}

export function fileNameFromSourceUrl(sourceUrl) {
  if (!sourceUrl?.trim()) return 'report.pdf';

  try {
    const name = new URL(sourceUrl.trim()).pathname.split('/').filter(Boolean).pop();
    if (name) return decodeURIComponent(name);
  } catch {
    // ponytail: fall back when URL is malformed
  }

  return 'report.pdf';
}

function resolveContentType(fileName) {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (lower.endsWith('.xls')) return 'application/vnd.ms-excel';
  if (lower.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (lower.endsWith('.doc')) return 'application/msword';
  return 'application/octet-stream';
}

export async function downloadFileFromSourceUrl(sourceUrl) {
  const url = sourceUrl.trim();
  const fileName = fileNameFromSourceUrl(url);
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to download from source URL (${response.status})`);
  }

  const blob = await response.blob();
  if (!blob.size) {
    throw new Error('Downloaded file is empty');
  }

  const contentType = blob.type && blob.type !== 'application/octet-stream'
    ? blob.type
    : resolveContentType(fileName);

  return new File([blob], fileName, { type: contentType });
}

export function buildEsgDocumentBlobPath({ companyName, year, fileName }) {
  const slug = companyName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (!slug) {
    throw new Error('Invalid company name for upload path');
  }

  return `${slug}/${year}/${fileName}`;
}

export async function uploadEsgDocument({ companyName, year, file }) {
  if (!file?.name) throw new Error('A file is required');
  if (!ESG_STORAGE_ACCOUNT_NAME) {
    throw new Error('REACT_APP_ESG_STORAGE_ACCOUNT_NAME is not configured');
  }

  const blobPath = buildEsgDocumentBlobPath({ companyName, year, fileName: file.name });
  const uploadUrl = await generatePreSignedUrl({
    storageAccountContainer: ESG_STORAGE_CONTAINER,
    permission: 'WRITE',
    blobName: ESG_STORAGE_ACCOUNT_NAME,
    blobPath,
  });

  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed (${uploadResponse.status})`);
  }

  return { blobPath, blobUrl: buildEsgDocumentBlobUrl(blobPath) };
}
